/**
 * web-sign-in-checkout D5 — the website's own signed-in transport and sign-in state.
 *
 * The website holds no credential but its cookie (`__Host-tw_session`, HttpOnly):
 * every request here sends `X-Telify-Web: 1` and lets the browser carry the
 * cookie. Flow requests name the flow by its `flowToken`, as the Mini App's do.
 * Not the Mini App's `apiFetch` (its global token and Telegram recovery).
 *
 * Who is signed in is the `['web', 'session']` query (`GET /api/site/session`).
 * Every signed-in query lives under `['web', 'client']`, and on every identity
 * change (sign-in, sign-out, a 401, a different user) those are dropped at once.
 */
import { useEffect, useState } from 'react';
import { useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import type { SiteSessionDto, SiteSignInResult } from '@telify/shared';

import { SiteFetchError, siteFetch, useSiteStudio } from './site';

export const sessionKeys = {
  session: ['web', 'session'] as const,
  nonce: ['web', 'sign-in-nonce'] as const,
};

/** The scope of every query read on the website session. */
export const CLIENT_SCOPE = ['web', 'client'] as const;

/** Called when a signed-in request answers 401: the session ended elsewhere. */
let onSessionEnded: (() => void) | null = null;

/** Wires the 401 handling to the query client (once, from the app). */
export function bindSessionEnd(queryClient: QueryClient): () => void {
  onSessionEnded = () => applyIdentity(queryClient, { signedIn: false });
  return () => {
    onSessionEnded = null;
  };
}

async function request<T>(url: string, init: RequestInit = {}): Promise<T> {
  const res = await fetch(url, {
    ...init,
    credentials: 'same-origin',
    headers: {
      Accept: 'application/json',
      'X-Telify-Web': '1',
      // web-account-pages D5: the server records the device's zone from this once a day.
      ...zoneHeader(),
      ...(init.body ? { 'Content-Type': 'application/json' } : {}),
      ...init.headers,
    },
  });
  if (res.status === 401) onSessionEnded?.();
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { error?: string; code?: string; errorCode?: string; message?: string };
    throw new SiteFetchError(res.status, body.code ?? body.errorCode ?? body.error, body.message ?? `HTTP ${res.status}`, body);
  }
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

/** The app's client routes the website may call (`/api/webapp/*`, the site-client allowance). */
export function webFetch<T>(path: string, init?: RequestInit): Promise<T> {
  return request<T>(`/api/webapp${path}`, init);
}

/** The website's signed-in catalog reads (`/api/site/client/*`). */
export function siteClientFetch<T>(path: string): Promise<T> {
  return request<T>(`/api/site/client${path}`);
}

/** A website mutation under `/api/site` (sign-in, sign-out). */
export function sitePost<T>(path: string, body?: unknown): Promise<T> {
  return request<T>(`/api/site${path}`, { method: 'POST', body: JSON.stringify(body ?? {}) });
}

/** Counts sign-ins and sign-outs here, so a session read sent before one cannot undo it. */
let identityChanges = 0;

/** A different person (or none) than the session last read drops every signed-in query. */
function dropIfSomeoneElse(queryClient: QueryClient, next: SiteSessionDto): void {
  const prev = queryClient.getQueryData<SiteSessionDto>(sessionKeys.session);
  const prevId = prev?.signedIn ? prev.user?.id : undefined;
  const nextId = next.signedIn ? next.user?.id : undefined;
  if (prevId !== nextId) queryClient.removeQueries({ queryKey: CLIENT_SCOPE });
}

/** Who is signed in, as the session last read; null when no one is. */
export function signedInId(queryClient: QueryClient): string | null {
  const known = queryClient.getQueryData<SiteSessionDto>(sessionKeys.session);
  return known?.signedIn ? (known.user?.id ?? null) : null;
}

/**
 * Records who is signed in; a different person (or none) drops every signed-in query first. Whether the server offers
 * the test account's sign-in belongs to the server, not the person, so it carries over when `next` doesn't say.
 */
export function applyIdentity(queryClient: QueryClient, next: SiteSessionDto): void {
  identityChanges += 1;
  dropIfSomeoneElse(queryClient, next);
  const devSignIn = next.devSignIn ?? queryClient.getQueryData<SiteSessionDto>(sessionKeys.session)?.devSignIn;
  queryClient.setQueryData(sessionKeys.session, devSignIn === undefined ? next : { ...next, devSignIn });
}

/** Reads who is signed in. Another tab may have signed someone else in; an answer sent before a sign-in or sign-out here is stale. */
async function readSession(queryClient: QueryClient): Promise<SiteSessionDto> {
  const sent = identityChanges;
  const next = await siteFetch<SiteSessionDto>('/session');
  const known = queryClient.getQueryData<SiteSessionDto>(sessionKeys.session);
  if (sent !== identityChanges && known) return known;
  dropIfSomeoneElse(queryClient, next);
  return next;
}

/** The name drawn for a signed-in client, and its initials. */
export function clientName(user: { firstName: string | null; lastName: string | null; username: string | null }) {
  const name = [user.firstName, user.lastName].filter(Boolean).join(' ') || user.username || 'You';
  const initials =
    ((user.firstName?.[0] ?? '') + (user.lastName?.[0] ?? '')).toUpperCase() || name.slice(0, 1).toUpperCase();
  return { name, initials };
}

export type WebSessionStatus = 'loading' | 'signedOut' | 'signedIn';

export function useWebSession() {
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: sessionKeys.session,
    queryFn: () => readSession(queryClient),
    staleTime: 60_000,
  });
  const data = query.data;
  const status: WebSessionStatus = !data ? (query.isError ? 'signedOut' : 'loading') : data.signedIn ? 'signedIn' : 'signedOut';
  return { status, user: data?.signedIn ? (data.user ?? null) : null, devSignIn: Boolean(data?.devSignIn) };
}

// ── Telegram Login (design D1): the library's global, never through `window.` ──

interface TelegramLoginResult {
  id_token?: string;
  error?: string;
}

interface TelegramLoginApi {
  auth(
    options: { client_id: number; scope: string[]; nonce: string; lang?: string },
    callback: (result: TelegramLoginResult) => void,
  ): void;
}

const LOGIN_SCRIPT = 'https://telegram.org/js/telegram-login.js';

function telegramLogin(): TelegramLoginApi | undefined {
  return (globalThis as unknown as { Telegram?: { Login?: TelegramLoginApi } }).Telegram?.Login;
}

let scriptRequested = false;
/** Loads Telegram's login library once, ahead of the click (its window must open inside the click). */
function loadTelegramLogin(): void {
  if (scriptRequested || telegramLogin() || typeof document === 'undefined') return;
  scriptRequested = true;
  const script = document.createElement('script');
  script.src = LOGIN_SCRIPT;
  script.async = true;
  script.onerror = () => {
    scriptRequested = false;
  };
  document.head.appendChild(script);
}

/** The browser's zone: the sign-in records it as the app's does, and later requests keep it current (D5). */
function zoneHeader(): Record<string, string> {
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return tz ? { 'X-Client-Timezone': tz } : {};
  } catch {
    return {};
  }
}

export type SignInState = 'idle' | 'pending' | 'error' | 'unavailable';

/**
 * web-site-factory D4: on an address that is not the studio's main one, sign-in
 * answers 409 SIGN_IN_ELSEWHERE with the main address; null for any other answer.
 */
function signInElsewhere(err: unknown): string | null {
  if (!(err instanceof SiteFetchError) || err.status !== 409 || err.code !== 'SIGN_IN_ELSEWHERE') return null;
  const mainUrl = err.body.mainUrl;
  return typeof mainUrl === 'string' && mainUrl.startsWith('https://') ? mainUrl : null;
}

/** Goes to the same path and query on the main address, where sign-in happens. */
function signInAt(mainUrl: string): void {
  window.location.assign(mainUrl.replace(/\/+$/, '') + window.location.pathname + window.location.search);
}

/**
 * Sign in with Telegram on this studio's bot. The one-time value is fetched
 * before the click and Telegram's library loaded, so the click opens the
 * window at once (a window opened after a wait is blocked by the browser).
 */
export function useTelegramSignIn(enabled = true) {
  const queryClient = useQueryClient();
  const { status: sessionStatus, devSignIn } = useWebSession();
  const clientId = useSiteStudio().data?.loginClientId ?? null;
  const wanted = enabled && sessionStatus === 'signedOut' && Boolean(clientId);
  const nonce = useQuery({
    queryKey: sessionKeys.nonce,
    queryFn: () => sitePost<{ nonce: string }>('/sign-in/start'),
    enabled: wanted,
    // The server keeps it five minutes; take a fresh one before then.
    staleTime: 4 * 60_000,
    refetchInterval: 4 * 60_000,
    gcTime: 0,
  });
  const [state, setState] = useState<SignInState>('idle');
  const [canMessage, setCanMessage] = useState<boolean | null>(null);
  // An open tab on an address that stopped being main: Sign in takes the visitor there.
  const elsewhere = signInElsewhere(nonce.error);

  useEffect(() => {
    if (wanted) loadTelegramLogin();
  }, [wanted]);

  const finish = async (call: () => Promise<SiteSignInResult>) => {
    try {
      const result = await call();
      setCanMessage(result.canMessage);
      applyIdentity(queryClient, { signedIn: true, user: result.user, devSignIn });
      setState('idle');
    } catch (err) {
      const mainUrl = signInElsewhere(err);
      if (mainUrl) return signInAt(mainUrl);
      setState('error');
    }
  };

  const signIn = () => {
    if (elsewhere) {
      signInAt(elsewhere);
      return;
    }
    const login = telegramLogin();
    const value = nonce.data?.nonce;
    if (!clientId) {
      setState('unavailable');
      return;
    }
    if (!login || !value) {
      setState('error');
      return;
    }
    setState('pending');
    login.auth({ client_id: Number(clientId), scope: ['profile', 'write'], nonce: value, lang: 'en' }, (result) => {
      // Used or not, a value is good for one attempt.
      queryClient.removeQueries({ queryKey: sessionKeys.nonce });
      if (!result?.id_token) {
        setState(result?.error === 'popup_closed' ? 'idle' : 'error');
        return;
      }
      void finish(() => sitePost<SiteSignInResult>('/sign-in', { idToken: result.id_token }));
    });
  };

  /** Dev only: the test account's sign-in, offered when the server has it. */
  const signInAsTestAccount = devSignIn
    ? () => {
        setState('pending');
        void finish(() => sitePost<SiteSignInResult>('/dev-sign-in', {}));
      }
    : null;

  const ready = (Boolean(clientId) && Boolean(nonce.data)) || elsewhere !== null;
  /**
   * For a place with one Sign in (the header, Message the studio): in development the test account, since dev's
   * bots are on Telegram's test server, which Telegram's sign-in window does not serve (Gabriel 10-27); otherwise Telegram.
   */
  const signInOneTap = signInAsTestAccount ?? signIn;
  const oneTapReady = signInAsTestAccount ? true : ready;
  return { signIn, signInAsTestAccount, signInOneTap, oneTapReady, state, ready, canMessage };
}

/**
 * Ends the website session here; the cookie goes and every signed-in query with
 * it. Resolves false, keeping the client signed in on screen, when the server
 * did not end it (a network failure or an error): a sign-out that only looked
 * done would leave the session live for the next person at this browser. A
 * session already gone (401) counts as signed out.
 */
export function useSignOut() {
  const queryClient = useQueryClient();
  return async (): Promise<boolean> => {
    try {
      await sitePost<void>('/sign-out');
    } catch (error) {
      if (!(error instanceof SiteFetchError && error.status === 401)) return false;
    }
    applyIdentity(queryClient, { signedIn: false });
    return true;
  };
}

export { SiteFetchError };
